import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { PlacedDesignElement } from '../../../api/tendApi';
import { clampPlacement, deriveAnchoredOffsets } from './designElementPosition';
import {
  DESIGN_ELEMENT_DEFAULT_OPACITY,
  designElementDepth,
  designElementFlipTransform,
  designElementOpacity,
} from '../../../utils/designElementLayers';
import ElementFrame, { ToolbarPlacement } from '../overlay-controls/ElementFrame';
import ElementToolbar from '../overlay-controls/ElementToolbar';

interface PlaceableDesignElementProps {
  placement: PlacedDesignElement;
  /** Canvas container — used to measure dimensions for drag math (in px). */
  containerRef: React.RefObject<HTMLDivElement>;
  /**
   * The editor's chrome host, a layer above the set elements. An element that
   * is BEHIND the text keeps its artwork at its true depth and draws its
   * selection chrome (outline, handles, X, pill) in here, so the chrome is
   * never under a title. Without a host the chrome stays on the element.
   */
  chromeHost?: HTMLElement | null;
  isSelected: boolean;
  /** Disable handles (used in non-editable contexts). */
  editable: boolean;
  onChange: (next: PlacedDesignElement) => void;
  onSelect: () => void;
  onDelete: () => void;
}

type Mode = 'drag' | 'resize' | 'rotate';

interface DragState {
  mode: Mode;
  pointerId: number;
  containerW: number;
  containerH: number;
  containerLeft: number;
  containerTop: number;
  startCenterX: number;
  startCenterY: number;
  startPlacement: PlacedDesignElement;
  startPointerX: number;
  startPointerY: number;
}

const SNAP_DEG = 5;

function snapRotation(deg: number): number {
  let d = (((deg + 180) % 360) + 360) % 360 - 180;
  for (const target of [-180, -90, 0, 90, 180]) {
    if (Math.abs(d - target) <= SNAP_DEG) return target;
  }
  return d;
}

/**
 * Compute CSS percentages for rendering — no canvas measurement needed.
 *
 * X axis is always natively % of width (CSS `left: X%` resolves to X% of
 * parent width). Y axis is the tricky one: anchored placements store offset
 * in % of WIDTH, so we convert to % of HEIGHT via the canvas aspect ratio.
 *
 * `canvasAspectHW` = canvas height / canvas width. For 4:5 it's 1.25; for
 * 9:16 it's ~1.78. We derive it lazily from the container in JS.
 */
function getPlacementCssPcts(
  p: PlacedDesignElement,
  canvasAspectHW: number,
): { leftPct: number; topPct: number; widthPct: number } {
  const widthPct = p.widthPct;

  let leftPct: number;
  if (!p.xAnchor) leftPct = p.xPct;
  else if (p.xAnchor === 'left') leftPct = p.xPct;
  else if (p.xAnchor === 'right') leftPct = 100 - p.xPct;
  else leftPct = 50 + p.xPct; // center anchor, signed offset

  let topPct: number;
  if (!p.yAnchor) {
    // Legacy: yPct already in % of height, use directly.
    topPct = p.yPct;
  } else {
    // Anchored: yPct is % of WIDTH; convert to % of HEIGHT.
    const safeAspect = canvasAspectHW > 0 ? canvasAspectHW : 1;
    const yInHeightPct = p.yPct / safeAspect;
    if (p.yAnchor === 'top') topPct = yInHeightPct;
    else if (p.yAnchor === 'bottom') topPct = 100 - yInHeightPct;
    else topPct = 50 + yInHeightPct;
  }

  return { leftPct, topPct, widthPct };
}

const PlaceableDesignElement: React.FC<PlaceableDesignElementProps> = ({
  placement,
  containerRef,
  chromeHost = null,
  isSelected,
  editable,
  onChange,
  onSelect,
  onDelete,
}) => {
  const dragRef = useRef<DragState | null>(null);

  // Lazy measurement — only happens when the user starts an interaction.
  // Rendering itself uses CSS percentages, so it never blocks on measurement.
  const measure = (): { w: number; h: number; left: number; top: number } => {
    const r = containerRef.current?.getBoundingClientRect();
    return r
      ? { w: r.width, h: r.height, left: r.left, top: r.top }
      : { w: 1, h: 1, left: 0, top: 0 };
  };

  // Movement threshold (px) before a drag actually starts firing onChange.
  // Below this, treat the gesture as a click — fixes the "tiny jitter on a
  // click registers as a drag" symptom.
  const DRAG_THRESHOLD_PX = 4;

  const startInteraction = (mode: Mode) => (e: React.PointerEvent) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    const r = measure();
    if (r.w <= 0 || r.h <= 0) return;

    // Compute current center position in canvas px (anchor-aware).
    let centerX: number;
    if (!placement.xAnchor) centerX = (placement.xPct / 100) * r.w;
    else if (placement.xAnchor === 'left') centerX = (placement.xPct / 100) * r.w;
    else if (placement.xAnchor === 'right') centerX = r.w - (placement.xPct / 100) * r.w;
    else centerX = r.w / 2 + (placement.xPct / 100) * r.w;

    let centerY: number;
    if (!placement.yAnchor) centerY = (placement.yPct / 100) * r.h;
    else if (placement.yAnchor === 'top') centerY = (placement.yPct / 100) * r.w;
    else if (placement.yAnchor === 'bottom') centerY = r.h - (placement.yPct / 100) * r.w;
    else centerY = r.h / 2 + (placement.yPct / 100) * r.w;

    const state: DragState = {
      mode,
      pointerId: e.pointerId,
      containerW: r.w,
      containerH: r.h,
      containerLeft: r.left,
      containerTop: r.top,
      startCenterX: centerX,
      startCenterY: centerY,
      startPlacement: placement,
      startPointerX: e.clientX,
      startPointerY: e.clientY,
    };
    dragRef.current = state;

    if (mode !== 'drag') onSelect();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* ignore — capture not available */
    }

    // Attach listeners synchronously so any pointermove/up that lands before
    // React's next render is handled. Capture mode (mode-specific closure
    // reads from `state` via dragRef).
    let movedBeyondThreshold = false;
    const onMove = (ev: PointerEvent) => {
      const s = dragRef.current;
      if (!s || ev.pointerId !== s.pointerId) return;
      const dxPx = ev.clientX - s.startPointerX;
      const dyPx = ev.clientY - s.startPointerY;

      // Deadband for drag mode only — resize/rotate users intend movement
      // immediately on grabbing those handles.
      if (s.mode === 'drag' && !movedBeyondThreshold) {
        if (Math.hypot(dxPx, dyPx) < DRAG_THRESHOLD_PX) return;
        movedBeyondThreshold = true;
      }

      if (s.mode === 'drag') {
        const newCenterX = s.startCenterX + dxPx;
        const newCenterY = s.startCenterY + dyPx;
        const anchored = deriveAnchoredOffsets(newCenterX, newCenterY, s.containerW, s.containerH);
        onChange(clampPlacement({ ...s.startPlacement, ...anchored }, s.containerW, s.containerH));
        return;
      }

      if (s.mode === 'resize') {
        const θ = (s.startPlacement.rotation * Math.PI) / 180;
        const localDx = dxPx * Math.cos(θ) + dyPx * Math.sin(θ);
        const widthDeltaPct = ((2 * localDx) / s.containerW) * 100;
        const nextWidth = s.startPlacement.widthPct + widthDeltaPct;
        onChange(clampPlacement({ ...s.startPlacement, widthPct: nextWidth }, s.containerW, s.containerH));
        return;
      }

      if (s.mode === 'rotate') {
        const screenCenterX = s.containerLeft + s.startCenterX;
        const screenCenterY = s.containerTop + s.startCenterY;
        const dx = ev.clientX - screenCenterX;
        const dy = ev.clientY - screenCenterY;
        const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        onChange(
          clampPlacement(
            { ...s.startPlacement, rotation: snapRotation(angleDeg) },
            s.containerW,
            s.containerH,
          ),
        );
      }
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
    const onEnd = (ev: PointerEvent) => {
      const s = dragRef.current;
      if (!s || ev.pointerId !== s.pointerId) return;
      dragRef.current = null;
      cleanup();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  };

  // Read canvas aspect ratio from the live DOM. We do this each render but
  // tolerate it returning 0/null (just falls back to aspect=1, which renders
  // correctly for square canvases and is "close enough" for the brief moment
  // before the parent paints; the next render after layout fixes it).
  const r = containerRef.current?.getBoundingClientRect();
  const canvasAspectHW = r && r.width > 0 ? r.height / r.width : 1;

  const { leftPct, topPct, widthPct } = getPlacementCssPcts(placement, canvasAspectHW);

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${leftPct}%`,
    top: `${topPct}%`,
    width: `${widthPct}%`,
    aspectRatio: `${placement.aspectRatio || 1} / 1`,
    transform: `translate(-50%, -50%) rotate(${placement.rotation}deg)`,
    transformOrigin: 'center center',
    pointerEvents: 'auto',
    cursor: editable ? 'move' : 'default',
    userSelect: 'none',
  };

  // Flip and opacity live on the artwork, not the wrapper: the wrapper
  // carries the handles, the X and the pill, which must never mirror or
  // fade, and the mirror sits UNDER the wrapper's rotate so a flipped arrow
  // stays flipped as it turns (utils/designElementLayers.ts).
  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none',
    opacity: designElementOpacity(placement),
    transform: designElementFlipTransform(placement),
  };

  // Handles share the frame's colour (white, dark edge) so every element's
  // selection reads the same.
  const handleBaseStyle: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(255,255,255,0.95)',
    border: '1px solid rgba(0,0,0,0.55)',
    boxShadow: '0 0 2px rgba(0,0,0,0.6)',
    pointerEvents: 'auto',
  };

  const handles = (
    <>
      <div
        onPointerDown={startInteraction('resize')}
        data-overlay-handle="resize"
        style={{
          ...handleBaseStyle,
          right: -6,
          bottom: -6,
          width: 12,
          height: 12,
          borderRadius: 2,
          cursor: 'nwse-resize',
        }}
        title="Drag to resize"
      />
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: -22,
          width: 1,
          height: 20,
          background: 'rgba(255,255,255,0.9)',
          transform: 'translateX(-0.5px)',
          pointerEvents: 'none',
        }}
      />
      <div
        onPointerDown={startInteraction('rotate')}
        data-overlay-handle="rotate"
        style={{
          ...handleBaseStyle,
          left: '50%',
          top: -28,
          width: 12,
          height: 12,
          borderRadius: '50%',
          transform: 'translateX(-50%)',
          cursor: 'grab',
        }}
        title="Drag to rotate (snaps every 90°)"
      />
    </>
  );

  // The pill: front/back, flip, opacity (the X stays on the frame's corner;
  // resize and rotate stay handles).
  const toolbar = (tbPlacement: ToolbarPlacement) => (
    <ElementToolbar
      kind="design"
      placement={tbPlacement}
      depth={designElementDepth(placement)}
      flipX={!!placement.flipX}
      opacity={typeof placement.opacity === 'number' && Number.isFinite(placement.opacity) ? placement.opacity : DESIGN_ELEMENT_DEFAULT_OPACITY}
      onChange={(patch) => onChange({ ...placement, ...patch })}
    />
  );

  const elementKey = `design:${placement.instanceId}`;
  const image = <img src={placement.url} alt="" draggable={false} style={imageStyle} />;

  // BEHIND the set elements: the artwork stays at its true depth (what you
  // see is what renders) while the selection chrome is drawn in the chrome
  // host above everything, so a behind element can always be grabbed by its
  // handles and its pill is never under a title. The shell shares the
  // wrapper's percentages, so it needs no measuring and follows a drag
  // render for render; it takes no pointer events itself, only its chrome.
  if (designElementDepth(placement) === 'back' && chromeHost) {
    return (
      <>
        <div
          data-design-element="true"
          data-design-element-id={placement.instanceId}
          data-overlay-element={elementKey}
          data-selected={isSelected && editable ? 'true' : undefined}
          style={wrapperStyle}
          onPointerDown={editable ? startInteraction('drag') : undefined}
          onClick={(e) => {
            if (!editable) return;
            e.stopPropagation();
            if (!isSelected) onSelect();
          }}
        >
          {image}
        </div>
        {isSelected && editable &&
          createPortal(
            <ElementFrame
              elementKey={elementKey}
              selected
              editable
              onSelect={onSelect}
              onDismiss={onDelete}
              dismissTitle="Remove element"
              boundsRef={containerRef}
              handles={handles}
              toolbar={toolbar}
              stopClickPropagation
              style={{ ...wrapperStyle, pointerEvents: 'none', cursor: 'default' }}
              wrapperProps={{ 'data-design-element-chrome': placement.instanceId }}
            >
              <div style={{ width: '100%', height: '100%' }} />
            </ElementFrame>,
            chromeHost,
          )}
      </>
    );
  }

  return (
    <ElementFrame
      elementKey={elementKey}
      selected={isSelected}
      editable={editable}
      onSelect={onSelect}
      onDismiss={onDelete}
      dismissTitle="Remove element"
      boundsRef={containerRef}
      handles={handles}
      toolbar={toolbar}
      stopClickPropagation
      style={wrapperStyle}
      wrapperProps={{
        'data-design-element': 'true',
        'data-design-element-id': placement.instanceId,
        onPointerDown: editable ? startInteraction('drag') : undefined,
      }}
    >
      {image}
    </ElementFrame>
  );
};

export default PlaceableDesignElement;
